import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MessagingContext from '../../providers/Messaging/Messaging.context';
import EmptyScreen from 'components/EmptyScreen';
import classNames from 'classnames';
import BuyerInfo from './BuyerInfo';
import { Button } from 'ncoded-component-library';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import SellerInfo from './SellerInfo';
import GroupMembers from './GroupMembers';
import NdaSigningProgress from '../NdaSigningProgress';
import { getImgUrl, RESIZED_WIDTH } from 'components/ResolveImage/utils';
import { Link } from 'react-router-dom';
import LinkToIcon from 'icons/LinkTo.icon';
import { getSellingPortfolio, isNdaNotSent } from 'utils';
import BuyerNdaCard from '../../../../components/BuyerNdaCard';
import { UpdateLeadNdaProps } from 'hooks/useNdaStatus';
import MessageFiles from 'components/MessageFiles';
import useCanAccessDataRoom from 'hooks/useCanAccessDataRoom';
import { NEW_WINDOW_PROPS } from 'data/global';

import './ContactPreview.styles.scss';
import './ContactPreview.styles.responsive.scss';

type ProfilePreviewProps = {
  className?: string;
};

const ProfilePreview: React.FC<ProfilePreviewProps> = (props) => {
  const { className } = props;

  const classes = classNames('contact-preview', className);

  const { t } = useTranslation();

  const {
    sessionId,
    currentSession,
    loadingSession,
    userSide,
    setContactOpen,
    updateCurrentSession,
  } = useContext(MessagingContext);

  const {
    isDirectMessage,
    interlocutor,
    isRepresentingSellSide,
    isGroupChat,
    sellRepresentatives,
    buyRepresentatives,
    deal,
    enquiryQuestions,
    signingStatus,
    signedDate,
    envelopeId,
    manuallySetNDAStatus,
    rejectionReason,
    envelopeTemplateName,
    leadId,
    hasDataRoomAccess: sessionHasDataRoomAccess,
    ndaProvider,
    agreementExecutionId,
    agreementExecuted,
  } = currentSession || {};

  const {
    _id: dealRoomId,
    dataRoomId,
    companyName,
    companyLogo,
    name: dealName,
    portfolioName,
    userId: ownerId,
  } = deal || {};

  const isInterlocutorSeller = !isDirectMessage && !isRepresentingSellSide;
  const showSellerPanel = !!companyName;

  const canAccessDataRoom = useCanAccessDataRoom();

  const hasDataRoomAccess =
    !!dataRoomId && canAccessDataRoom(deal, sessionHasDataRoomAccess);

  const RenderedMessageFiles = useMemo(
    () => (
      <MessageFiles
        dataRoomId={dataRoomId}
        dataRoomName={dealName}
        ownerId={ownerId}
        userSide={userSide}
        hasDataRoomAccess={hasDataRoomAccess}
      />
    ),
    [dataRoomId, dealName, ownerId, userSide, hasDataRoomAccess],
  );

  const updateNdaProps = useCallback(
    (leadNdaProps: UpdateLeadNdaProps) => updateCurrentSession(leadNdaProps),
    [updateCurrentSession],
  );

  if (loadingSession) return null;

  if (!sessionId || !currentSession)
    return (
      <EmptyScreen
        className={classNames('contact-preview__empty-screen', className)}
        content={t('selectPersonToSeeProfile')}
      />
    );

  return (
    <div className={classes}>
      <div className="contact-preview__actions">
        <Button
          variant="icon"
          className="chat-arrow"
          icon={ArrowleftIcon}
          onClick={() => setContactOpen((old) => !old)}
          aria-label="Back to chat."
        />
      </div>

      {isDirectMessage ? (
        <>
          <BuyerInfo buyer={interlocutor} />
          {RenderedMessageFiles}
        </>
      ) : (
        <>
          {!isInterlocutorSeller && (
            <div className="contact-preview__deal-header">
              <p>{portfolioName || dealName}</p>
              <Link to={getSellingPortfolio(dealRoomId)} {...NEW_WINDOW_PROPS}>
                {t('portfolio')}
                <LinkToIcon />
              </Link>
            </div>
          )}

          {isInterlocutorSeller ? (
            showSellerPanel && (
              <SellerInfo
                title={companyName}
                imageUrl={getImgUrl(companyLogo?.url, RESIZED_WIDTH[240])}
              />
            )
          ) : isGroupChat ? (
            <GroupMembers
              sellRepresentatives={sellRepresentatives}
              buyRepresentatives={buyRepresentatives}
            />
          ) : (
            <BuyerInfo
              buyer={interlocutor}
              enquiryQuestions={enquiryQuestions}
            />
          )}

          {!isGroupChat &&
            (!isInterlocutorSeller ? (
              <NdaSigningProgress />
            ) : (
              // the heading lives here rather than in BuyerNdaCard, which the buyer portfolio
              // aside also renders; the card draws nothing until an NDA has been sent, so the
              // section only appears once there is something to head
              !isNdaNotSent(signingStatus) && (
                <div className="contact-preview__nda">
                  <p className="title">{t('nonDisclosureAgreement')}</p>

                  <BuyerNdaCard
                    leadId={leadId}
                    signingStatus={signingStatus}
                    envelopeId={envelopeId}
                    signedDate={signedDate}
                    manuallySetNDAStatus={manuallySetNDAStatus}
                    envelopeTemplateName={envelopeTemplateName}
                    rejectionReason={rejectionReason}
                    nativeNdaProps={{
                      ndaProvider,
                      agreementExecutionId,
                      agreementExecuted,
                    }}
                    updateNdaProps={updateNdaProps}
                  />
                </div>
              )
            ))}

          {RenderedMessageFiles}
        </>
      )}
    </div>
  );
};

export default ProfilePreview;
